import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { ANALYSIS_STAGES } from '../common/utils'
import LiveTooltip from 'live/components/ui/tooltip'
import {
    anyLowRatingWithoutDeviation,
    anyActiveRatingWithoutAScore,
    anythingEmptyInGeneralInformation,
    checkForInvalidWeighings
} from './analysisValidations'
import { ParamsContext, AssetTreeContext } from '../qualityMonitoring/qualityMonitoring'
import { EmptyFields } from './analysisTypes'
import ConfirmationDialog from 'live/components/actions/ui/confirmation-dialog'
import { OverweightDialogContent } from './overweightDialogContent'

const STATUS_OPTIONS = {
    WAITING: 'isWaiting',
    CURRENT: 'isCurrent',
    CHECKED: 'isChecked'
} as const
type statusKeys = keyof typeof STATUS_OPTIONS
type statusTypes = typeof STATUS_OPTIONS[statusKeys]
interface IStatusBar {
    generalInfo: GeneralInfo
    ratingInfo: RatingInfo
    currentStage: stagesTypes
    setCurrentStage: (stage: stagesTypes) => void
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}
export function StatusBar({
    currentStage,
    setCurrentStage,
    generalInfo,
    ratingInfo,
    emptyFields,
    setEmptyFields
}: IStatusBar): JSX.Element {
    const urlParams = useContext(ParamsContext).split('/')
    const assetTree = useContext(AssetTreeContext)
    const [showOverweightModal, setShowOverweightModal] = useState<boolean>(false)

    function getNextButtonDisabledStatus(): boolean {
        switch (currentStage) {
            case 'general information':
                return anythingEmptyInGeneralInformation(generalInfo)
            case 'rating':
                return (
                    anyActiveRatingWithoutAScore(ratingInfo, urlParams, assetTree) ||
                    anyLowRatingWithoutDeviation(ratingInfo)
                )
            case 'result':
                return true
            default:
                return false
        }
    }

    const disabledStatus = getNextButtonDisabledStatus()
    const tooltipMessage =
        currentStage === ANALYSIS_STAGES.GENERAL_INFO
            ? 'Please make sure all fields are filled correctly'
            : 'Please give a score to all atributes'

    function handleClickOnNext(): void {
        const emptyFieldsList = getEmptyfieldsList()
        setEmptyFields({
            trigger: !!emptyFieldsList,
            fields: emptyFieldsList
        })
        if (disabledStatus) {
            return null
        } else if (currentStage === ANALYSIS_STAGES.GENERAL_INFO) {
            if (isOverweightData()) {
                setShowOverweightModal(true)

                return
            }

            setCurrentStage(ANALYSIS_STAGES.RATING)
        } else {
            setCurrentStage(ANALYSIS_STAGES.RESULT)
        }
    }

    function isOverweightData(): boolean {
        let overweight = false
        generalInfo.weighings.forEach(weighing => {
            if (weighing.weight > Number(weighing.grammage) + Number(weighing.grammage) * 0.1) {
                overweight = true
            }
        })

        return overweight
    }

    function getEmptyfieldsList(): string[] {
        const emptyFieldsList: string[] = emptyFields.fields
        if (
            generalInfo.isDelayed &&
            !generalInfo.justificationText &&
            !generalInfo.justificationPlannedEvent?.length &&
            disabledStatus &&
            !emptyFieldsList.includes('justificationText')
        ) {
            emptyFieldsList.push('justificationText')
        }
        if (!Number(generalInfo.batch) && !emptyFieldsList.includes('batch')) {
            emptyFieldsList.push('batch')
        }
        if (!generalInfo.sku && !emptyFieldsList.includes('sku')) {
            emptyFieldsList.push('sku')
        }
        if (!generalInfo.reference && generalInfo.reference !== false && !emptyFieldsList.includes('reference')) {
            emptyFieldsList.push('reference')
        }
        if (
            (generalInfo.weighings.length < 2 || checkForInvalidWeighings(generalInfo.weighings)) &&
            !emptyFieldsList.includes('weighings')
        ) {
            emptyFieldsList.push('weighings')
        }
        if (
            !generalInfo.participants.filter(participant => participant).length &&
            !emptyFieldsList.includes('participants')
        ) {
            emptyFieldsList.push('participants')
        }
        if (
            generalInfo.justificationText === 'OTHERS' &&
            (!generalInfo.justificationObservationText ||
                generalInfo.justificationObservationText === '' ||
                generalInfo.justificationObservationText.toString().match(/^\s+$/g)) &&
            !emptyFieldsList.includes('justificationObservationText')
        ) {
            emptyFieldsList.push('justificationObservationText')
        }

        return emptyFieldsList
    }

    const handleCancel = (): void => {
        setShowOverweightModal(false)
    }

    const handleOverWeightModalAply = (): void => {
        setCurrentStage(ANALYSIS_STAGES.RATING)
    }

    return (
        <StyledStatusBar className={'statusBar'}>
            <button
                className="statusBar--return"
                disabled={currentStage === ANALYSIS_STAGES.GENERAL_INFO}
                onClick={() =>
                    setCurrentStage(
                        currentStage === ANALYSIS_STAGES.RATING ? ANALYSIS_STAGES.GENERAL_INFO : ANALYSIS_STAGES.RATING
                    )
                }
            >
                <i className="fas fa-chevron-left" />
                {i18n('return')}
            </button>
            <div className="statusBar--bar">
                <BarMarker
                    name={i18n(ANALYSIS_STAGES.GENERAL_INFO)}
                    status={
                        currentStage === ANALYSIS_STAGES.GENERAL_INFO ? STATUS_OPTIONS.CURRENT : STATUS_OPTIONS.CHECKED
                    }
                />
                <BarMarker
                    name={i18n(ANALYSIS_STAGES.RATING)}
                    status={
                        currentStage === ANALYSIS_STAGES.GENERAL_INFO
                            ? STATUS_OPTIONS.WAITING
                            : currentStage === ANALYSIS_STAGES.RATING
                            ? STATUS_OPTIONS.CURRENT
                            : STATUS_OPTIONS.CHECKED
                    }
                />
                <BarMarker
                    name={i18n(ANALYSIS_STAGES.RESULT)}
                    status={currentStage === ANALYSIS_STAGES.RESULT ? STATUS_OPTIONS.CURRENT : STATUS_OPTIONS.WAITING}
                    hideLine={true}
                />
            </div>
            {currentStage === ANALYSIS_STAGES.RESULT ? null : (
                <LiveTooltip position="top" content={i18n(tooltipMessage)} hide={!disabledStatus}>
                    <button
                        className={`statusBar--next ${disabledStatus ? 'disableButton' : ''}`}
                        onClick={handleClickOnNext}
                    >
                        {i18n('next')}
                        <i className="fas fa-chevron-right" />
                    </button>
                </LiveTooltip>
            )}
            <ConfirmationDialog
                show={showOverweightModal}
                onHide={handleCancel}
                title={i18n('Overweight alert!')}
                btnStyle={'primary'}
                cancelText={i18n('cancel')}
                className={'stoppedLine-confirmation-modal modal-sm'}
                content={<OverweightDialogContent />}
                btnText={i18n('Continue')}
                apply={handleOverWeightModalAply}
                disableCancel={false}
            />
        </StyledStatusBar>
    )
}

interface IBarMarker {
    name: string
    status: statusTypes
    hideLine?: boolean
}
function BarMarker({ name, status, hideLine = false }: IBarMarker): JSX.Element {
    let markerIcon

    switch (status) {
        case STATUS_OPTIONS.CHECKED:
            markerIcon = <i className="fas fa-check-circle" />
            break
        case STATUS_OPTIONS.CURRENT:
            markerIcon = <i className="fas fa-dot-circle" />
            break

        default:
            markerIcon = <i className="fas fa-circle" />
            break
    }

    return (
        <div className={`statusBar--bar__marker ${status}`}>
            {markerIcon}
            <span className={'statusBar--bar__marker--text'}>{name}</span>
            {hideLine ? null : <div className="statusBar--bar__marker--line" />}
            {status === STATUS_OPTIONS.CHECKED ? <div className="statusBar--bar__marker--checkedLine" /> : null}
        </div>
    )
}
const StyledStatusBar = styled.div`
    --duration: 0.3s;
    --transition: all var(--duration) linear;
    display: flex;
    align-items: center;
    font-size: 14px;
    padding: 40px 0;
    .alertText {
        color: #fdd0cf;
    }
    .statusBar {
        &--return {
            i {
                margin-right: 8px;
            }
        }
        &--next {
            i {
                margin-left: 8px;
            }
        }
        &--return,
        &--next {
            color: ${({ theme }) => theme['blue-500']};
            background: transparent;
            border: none;
            display: flex;
            align-items: center;
            text-transform: capitalize;
            transition: var(--transition);
            font-family: ${({ theme }) => theme['font-semibold']};
            &.disableButton,
            &:disabled {
                color: ${({ theme }) => theme['blue-200']};
            }
            &:hover {
                transform: scale(1.2);
            }
            &:active {
                transform: scale(1);
            }
        }
        &--bar {
            display: flex;
            justify-content: space-between;
            flex-grow: 1;
            margin: 0 -5%;
            pointer-events: none;
            &__marker {
                color: ${({ theme }) => theme['gray-500']};
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                position: relative;
                flex: 1;
                transition: var(--transition);
                &--checkedLine,
                &--line {
                    position: absolute;
                    left: 58%;
                    top: 20%;
                    width: 84%;
                    height: 2px;
                    background: ${({ theme }) => theme['gray-500']};
                    transition: var(--transition);
                }
                &--checkedLine {
                    animation: grow var(--duration) linear;
                    background: ${({ theme }) => theme['green-700']};
                }
                &.${STATUS_OPTIONS.CHECKED} {
                    color: ${({ theme }) => theme['green-700']};
                }
                &.${STATUS_OPTIONS.CURRENT} {
                    color: ${({ theme }) => theme['gray-800']};
                    font-family: ${({ theme }) => theme['font-semibold']};
                    font-size: 18px;
                    i {
                        font-size: 22px;
                    }
                }
                &--text {
                    margin-top: 5px;
                    text-transform: capitalize;
                }
                i {
                    font-size: 18px;
                    transition: var(--transition);
                }
            }
        }
    }
    @keyframes grow {
        from {
            transform: translateX(-50%) scale(0, 1);
        }
        to {
            transform: translateX(0%) scale(1, 1);
        }
    }
`
